// Search and recent players (spec/dashboard.md section 11.1; spec/api-conventions.md section 16.2): one box whose value's
// shape picks the exact lookups by binding (players/idKind.ts), asked in order, with a "Search as" override; the lookup is
// kept in the URL (`?value=`, plus `&by=` for the override) — the first hit opens the player page, none says so in place
// naming the kinds tried — and the most recently
// seen players of the environment below (production included, no cursor by design); Export… (M7 slice 7) queues a
// players.export job and offers its file.
import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { ops } from '../../api/operations';
import type { Page, Player } from '../../api/types';
import { useAuth } from '../../auth/AuthContext';
import { enumName, shortPlayerId } from '../../players/model';
import { describeIdKind, guessIdKinds, isIdKind, type IdKind } from '../../players/idKind';
import { useReleaseInfo } from '../../players/releaseCatalog';
import { useGame } from '../../shell/GameContext';
import { routes } from '../../shell/routes';
import { EmptyState } from '../../ui/Banner';
import { Button } from '../../ui/Button';
import { DataGrid, type Column } from '../../ui/DataGrid';
import { Select, TextInput } from '../../ui/inputs';
import { formatInstant, useTimeZone } from '../../ui/time';
import { useQuery } from '../../workspace/cache';
import { errorText } from './commands';
import { PlayersExportDialog } from './PlayersExportDialog';
import './players.css';
import { LoadError } from '../../ui/PageStates';
import { Field } from '../../ui/Field';
import { Download, Info, Lock, RefreshCw, Search, Users } from '../../ui/icons';

/** The "Search as" override; '' (Auto) asks the kinds `guessIdKinds` gives, in order. */
export const SEARCH_AS = [
  { value: '', label: 'Auto' },
  { value: 'user_id', label: 'User id' },
  { value: 'device', label: 'Device id' },
  { value: 'nutaku', label: 'Nutaku id' },
  { value: 'custom', label: 'Custom id' },
  { value: 'name', label: 'Name' },
] as const;

const PLATFORM_ENUM = 'SmartObjects.BalancyPlatform';

type Lookup = { state: 'idle' } | { state: 'loading' } | { state: 'none'; kinds: IdKind[]; value: string } | { state: 'error'; message: string };

export function PlayersPage() {
  const { env: envSlug = '' } = useParams();
  const { org, game, can } = useGame();
  const canRead = can('players_read', envSlug);
  const [exporting, setExporting] = useState(false);
  return (
    <section className="env-page players-page" data-testid="players">
      <div className="page-head">
        <h1>Players</h1>
        <code className="page-meta">{envSlug}</code>
        <span className="spacer" />
        {canRead && (
          <Button icon={<Download aria-hidden="true" />} onClick={() => setExporting(true)} title="Every player with its saves as gzip NDJSON (a job)">
            Export…
          </Button>
        )}
      </div>
      {exporting && <PlayersExportDialog env={envSlug} onClose={() => setExporting(false)} />}
      {!canRead ? (
        <EmptyState icon={<Lock />} title="You cannot read players here" body={`Reading the players of “${envSlug}” needs the 'players_read' permission on this environment.`} />
      ) : (
        <>
          <PlayerSearch envSlug={envSlug} org={org} gameSlug={game.slug} />
          <RecentPlayers envSlug={envSlug} />
        </>
      )}
    </section>
  );
}

function PlayerSearch({ envSlug, org, gameSlug }: { envSlug: string; org: string; gameSlug: string }) {
  const { client } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const rawBy = params.get('by') ?? '';
  const urlBy = isIdKind(rawBy) ? rawBy : ''; // '' = Auto; an old `?by=&value=` link is the override
  const urlValue = params.get('value') ?? '';
  const [by, setBy] = useState<string>(urlBy);
  const [value, setValue] = useState(urlValue);
  const [lookup, setLookup] = useState<Lookup>({ state: 'idle' });
  const [attempt, setAttempt] = useState(0);

  // the lookup runs from the URL: a shared link (or a reload) asks the same question; the kinds are asked in order and the
  // first hit opens the player; an error aborts
  useEffect(() => {
    setBy(urlBy);
    setValue(urlValue);
    if (!urlValue.trim()) {
      setLookup({ state: 'idle' });
      return;
    }
    let live = true;
    const kinds: IdKind[] = urlBy ? [urlBy] : guessIdKinds(urlValue);
    setLookup({ state: 'loading' });
    (async () => {
      try {
        for (const kind of kinds) {
          const page = await client.data(ops.findPlayers, { path: { org, game: gameSlug, env: envSlug }, query: { by: kind, value: urlValue } });
          if (!live) return;
          const items = ((page as unknown as Page<Player>).items ?? []) as Player[];
          if (items.length > 0) {
            navigate(routes.player(org, gameSlug, envSlug, items[0]!.id), { replace: true });
            return;
          }
        }
        setLookup({ state: 'none', kinds, value: urlValue });
      } catch (e: unknown) {
        if (live) setLookup({ state: 'error', message: errorText(e) });
      }
    })();
    return () => {
      live = false;
    };
  }, [client, org, gameSlug, envSlug, urlBy, urlValue, navigate, attempt]);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const v = value.trim();
    if (!v) return;
    if (v === urlValue && by === urlBy) {
      setAttempt((n) => n + 1); // the same lookup again: the URL does not move, so re-run it
      return;
    }
    const next = new URLSearchParams({ value: v });
    if (by) next.set('by', by);
    setParams(next);
  };

  return (
    <form className="players-search" data-testid="players-search" onSubmit={submit} role="search" aria-label="Find a player">
      <div className="page-toolbar players-search-row">
        <Field label="Find a player" className="players-search-value">
          <TextInput
            data-testid="players-search-value"
            aria-label="Find a player"
            placeholder="A user id, Nutaku id, device id, custom id or name"
            value={value}
            onChange={(e) => setValue(e.target.value)}
          />
        </Field>
        <Field label="Search as" className="players-search-by">
          <Select data-testid="players-search-by" aria-label="Search as" value={by} onChange={(e) => setBy(e.target.value)}>
            {SEARCH_AS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Select>
        </Field>
        <Button type="submit" variant="primary" icon={<Search aria-hidden="true" />} disabled={!value.trim()} busy={lookup.state === 'loading'}>
          Search
        </Button>
      </div>
      <div data-testid="players-search-result" aria-live="polite">
        {lookup.state === 'loading' && <p className="loading-line players-looking"><span className="spinner" aria-hidden="true" /> Looking up…</p>}
        {lookup.state === 'none' && (
          <div className="banner players-none">
            <Info className="banner-icon" aria-hidden="true" />
            <div className="banner-body">
              No player found <span className="muted">for “{lookup.value}” — looked it up as {lookup.kinds.map(describeIdKind).join(', ')}.</span>
            </div>
          </div>
        )}
        {lookup.state === 'error' && (
          <LoadError>{lookup.message}</LoadError>
        )}
      </div>
    </form>
  );
}

function RecentPlayers({ envSlug }: { envSlug: string }) {
  const { client } = useAuth();
  const { org, game, offsets } = useGame();
  const [zone] = useTimeZone();
  const release = useReleaseInfo(envSlug);
  const q = useQuery(`branch:${game.id}:env:${envSlug}:players:recent`, () => client.data(ops.recentPlayers, { path: { org, game: game.slug, env: envSlug }, query: { limit: 200 } }));
  const rows = (((q.data as unknown as Page<Player> | undefined)?.items ?? []) as Player[]);
  const at = (v: string | null | undefined) => formatInstant(v, zone, { offsets });
  const columns: Column<Player>[] = [
    {
      key: 'player',
      header: 'Player',
      fixed: true,
      render: (p) => (
        <Link to={routes.player(org, game.slug, envSlug, p.id)} title={p.id}>
          <code>{shortPlayerId(p.id)}</code>
        </Link>
      ),
      cellText: (p) => p.id,
    },
    { key: 'lastSeen', header: 'Last seen', render: (p) => at(p.lastSeenAt), sortValue: (p) => p.lastSeenAt ?? '' },
    { key: 'created', header: 'Created', render: (p) => at(p.createdAt), sortValue: (p) => p.createdAt ?? '' },
    { key: 'country', header: 'Country', render: (p) => p.country || '—' },
    { key: 'platform', header: 'Platform', render: (p) => (p.lastPlatform === null || p.lastPlatform === undefined ? '—' : enumName(release.enums, PLATFORM_ENUM, p.lastPlatform)) },
    { key: 'online', header: 'Online', render: (p) => (p.online ? <span className="status-dot status-live">Online</span> : <span className="status-dot status-off">Offline</span>) },
    { key: 'sessions', header: 'Sessions', align: 'right', render: (p) => String(p.sessions?.active ?? 0) },
  ];
  return (
    <section className="players-recent" data-testid="recent-players" aria-label="Recent players">
      <div className="section-head">
        <h2>Recent players</h2>
        <span className="muted">The most recently seen first</span>
        <span className="spacer" />
        <Button size="s" icon={<RefreshCw aria-hidden="true" />} onClick={() => q.refetch()} busy={q.loading && !!q.data}>
          Refresh
        </Button>
      </div>
      {q.error ? (
        <LoadError>{errorText(q.error)}</LoadError>
      ) : (
        <DataGrid id="players-recent" columns={columns} rows={rows} rowKey={(p) => p.id} loading={q.loading && !q.data} emptyState={<EmptyState icon={<Users />} title="No player seen yet" body="Players appear here once a device signs in to this environment." />} />
      )}
    </section>
  );
}
